/**
 * TimelineItemCard コンポーネント
 *
 * タイムラインアイテム（作品またはイベント）を表示するカードコンポーネント。
 */

import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { getGenreLabelClass } from '@/lib/genre-colors';
import { getTimelineItemGenre } from '@/lib/timeline-data';
import type { TimelineItem } from '@/types/domain';
import { getLocalizedText } from '@/lib/i18n-helpers';

/**
 * TimelineItemCard コンポーネントのProps
 */
export interface TimelineItemCardProps {
  /** タイムラインアイテム */
  item: TimelineItem;

  /** 言語設定 */
  language: 'ja' | 'en';

  /** 現在のフィルタ状態を含むURL（詳細ページから戻る用） */
  currentFilterUrl?: string;

  /** カスタムクラス */
  className?: string;
}

/**
 * 日付を表示用にフォーマット
 *
 * @param dateStr - ISO8601形式の日付文字列
 * @returns フォーマットされた日付文字列
 */
function formatDisplayDate(dateStr: string): string {
  // "1966" → "1966"
  // "1966-05" → "1966年5月"
  // "1966-05-10" → "1966年5月10日"
  const parts = dateStr.split('-');

  if (parts.length === 1) {
    return parts[0] || dateStr;
  } else if (parts.length === 2) {
    return `${parts[0] || ''}年${parseInt(parts[1] || '1', 10)}月`;
  } else if (parts.length === 3) {
    return `${parts[0] || ''}年${parseInt(parts[1] || '1', 10)}月${parseInt(parts[2] || '1', 10)}日`;
  }

  return dateStr;
}

/**
 * TimelineItemCard コンポーネント
 *
 * 使用例:
 * ```tsx
 * <TimelineItemCard item={timelineItem} language="ja" />
 * ```
 */
export function TimelineItemCard({
  item,
  language,
  currentFilterUrl,
  className,
}: TimelineItemCardProps) {
  const title = getLocalizedText(item.title, language);
  const formattedDate = formatDisplayDate(item.date);
  const titleStr = typeof title === 'string' ? title : '';
  const genre = getTimelineItemGenre(item, language);

  return (
    <Link
      to={item.link || '#'}
      state={{ from: currentFilterUrl || '/timeline' }}
      className={cn(
        'block rounded-lg border border-border bg-card p-4',
        'transition-[border-color,box-shadow] duration-200',
        'hover:border-primary hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className
      )}
      aria-label={`${titleStr} - ${formattedDate}`}
      data-testid={`timeline-item-${item.id.split('/').pop()}`}
    >
      {/* タイトル */}
      <h3 className="text-sm font-semibold text-card-foreground truncate">
        {title}
      </h3>

      {/* 日付とジャンルラベル */}
      <div className="mt-1 flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{formattedDate}</p>
        {genre && (
          <span
            className={cn(
              'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
              getGenreLabelClass(genre)
            )}
          >
            {genre}
          </span>
        )}
      </div>
    </Link>
  );
}
